/** Share for review (PRD §22.4, M3): pick a contact → private guest link → WhatsApp (wa.me), copy or email. */
import { useState } from "react";
import type { ShareLink } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Button, EmptyState, Field, Loading, Modal } from "../../components/ui";
import { useApi, useMutation } from "../../data/hooks";
import { useContacts } from "../../data/queries";
import { dateTime } from "../../lib/format";

export function ShareSheet({ versionId, onClose }: { versionId: string; onClose: () => void }) {
  const api = useApi();
  const toast = useToast();
  const contacts = useContacts();
  const [contactId, setContactId] = useState<string | undefined>();
  const [follow, setFollow] = useState(false);
  const [link, setLink] = useState<ShareLink | null>(null);
  const create = useMutation(() => api.createShareLink({ scope_type: "version", scope_id: versionId, contact_id: contactId, follow_latest: follow, expires_in_days: 14 }), {
    onSuccess: setLink, onError: (e) => toast.error(e, "Couldn't create the link"),
  });
  const copy = async () => {
    if (!link) return;
    try { await navigator.clipboard.writeText(link.url); toast.ok("Link copied."); } catch { toast.show({ tone: "info", text: link.url }); }
  };
  return (
    <Modal title="Share for review" variant="sheet" onClose={onClose}>
      {link ? (
        <div className="stack">
          <p>Private link{link.expires_at ? ` — expires ${dateTime(link.expires_at)}` : ""}. Guests can watch, leave timestamped notes and say “Looks good”; they can't approve.</p>
          <a className="btn btn-primary btn-lg btn-block" href={link.whatsapp_url} target="_blank" rel="noreferrer"><Icon name="send" size={20} /><span>Send via WhatsApp</span></a>
          <div className="row">
            <Button variant="secondary" icon="link" onClick={() => void copy()}>Copy link</Button>
            <a className="btn btn-secondary btn-md" href={`mailto:?subject=${encodeURIComponent("Could you review this before it goes out?")}&body=${encodeURIComponent(link.url)}`}>Email</a>
          </div>
        </div>
      ) : (
        <div className="stack">
          {contacts.isLoading ? <Loading /> : (contacts.data?.length ? (
            <fieldset className="contact-pick">
              <legend className="field-label">Who is it for?</legend>
              {contacts.data.map((c) => (
                <label key={c.id} className="radio-row">
                  <input type="radio" name="contact" checked={contactId === c.id} onChange={() => setContactId(c.id)} />
                  <span><b>{c.name}</b> <span className="muted small">{[c.role?.replace(/_/g, " "), c.languages.join(", ").toUpperCase()].filter(Boolean).join(" · ")}</span></span>
                </label>
              ))}
              <label className="radio-row"><input type="radio" name="contact" checked={!contactId} onChange={() => setContactId(undefined)} /><span>Someone else (just make a link)</span></label>
            </fieldset>
          ) : <EmptyState icon="user" title="No contacts yet">Add speakers and reviewers in Settings → Contacts. You can still make a link.</EmptyState>)}
          <Field label="Link follows">{(id) => (
            <select id={id} value={follow ? "latest" : "this"} onChange={(e) => setFollow(e.target.value === "latest")}>
              <option value="this">This exact version</option><option value="latest">The latest version of this episode</option>
            </select>
          )}</Field>
          <Button variant="primary" size="lg" block busy={create.pending} onClick={() => void create.mutate()} icon="share">Create private link</Button>
        </div>
      )}
    </Modal>
  );
}
